import { notSupportedReason } from '@mapbox/mapbox-gl-supported';
import classNames from 'classnames';
import MapboxGL, { type StyleSpecification } from 'mapbox-gl';
import { ComponentProps, CSSProperties, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactMapGL, { AttributionControl, type MapRef, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useResizeObserver from '@alltrails/core/hooks/useResizeObserver';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import getMapboxAccessToken from '@alltrails/core/utils/getMapboxAccessToken';
import logError from '@alltrails/core/utils/logError';
import Dialog from '@alltrails/denali/components/Dialog';
import { useExperiments } from '@alltrails/experiments';
import useMapStyleLoaded from '../../hooks/useMapStyleLoaded';
import { useAppSelector } from '../../redux';
import type { AllTrailsBounds, AllTrailsCenter } from '../../types/Geo';
import type { Images } from '../../types/Images';
import { type DynamicStyleId, StaticStyleId } from '../../types/Styles';
import { maxPitch, maxPitchFor3dTerrain } from '../../utils/constants';
import getInitialViewState from '../../utils/getInitialViewState';
import AtmosphereStyle from '../AtmosphereStyle';
import DynamicStyleOverlays from '../DynamicStyleOverlays';
import EmptyChevronLayers from '../Polyline/EmptyChevronLayers';
import EmptyPolylineLayers from '../Polyline/EmptyPolylineLayers';
import StaticStyle from '../StaticStyle';
import { onMoveEndHandler, onStyleDataHandler } from './eventHandlers';
import styles from './styles/styles.module.scss';

type ControlsConfig = Pick<
  React.ComponentProps<typeof ReactMapGL>,
  'dragPan' | 'dragRotate' | 'scrollZoom' | 'touchZoomRotate' | 'touchPitch' | 'keyboard' | 'doubleClickZoom' | 'boxZoom' | 'interactive'
>;
type Props = {
  baseMapStyle: string | StyleSpecification;
  bottomPadding?: number; // Used when the map is being slightly covered by something like the top of a mobile drawer
  className?: string;
  children?: React.ReactNode;
  controlsConfig?: ControlsConfig;
  cursor?: string;
  dimensionsTheme?: 'default' | 'compact';
  disableDefaultAttribution?: boolean; // Hide the default AttributionControl (use when rendering custom attribution)
  disableInteraction?: boolean;
  fog?: ComponentProps<typeof ReactMapGL>['fog'];
  handleOnLoad?: () => void;
  id: string;
  // The images of any children of BaseMap. These are used in onStyleDataHandler and must be loaded here to avoid race conditions between Mapbox image and layer loading.
  // https://docs.mapbox.com/mapbox-gl-js/example/add-image
  images?: Images; // Some images have been transitioned to a new svg based pattern - see CuratedWaypoints or StartEndMarker for examples
  initialBounds?: AllTrailsBounds;
  initialCenter?: AllTrailsCenter;
  is3dActive?: boolean;
  onMoveEnd?: ({ center, bounds, zoom }) => void;
  overlaysExcludedGeoBoundaryIds?: number[];
  overlaysExcludedTrailIds?: number[];
  preventReuseMaps?: boolean;
  staticStyleIds: StaticStyleId[];
  supportedDynamicStyleIds?: DynamicStyleId[];
  terrain?: ComponentProps<typeof ReactMapGL>['terrain'];
} & Pick<React.ComponentProps<typeof ReactMapGL>, 'cooperativeGestures' | 'onDrag' | 'onTouchMove' | 'onZoomEnd' | 'onDragEnd' | 'onTouchEnd'>;

const BaseMapReact = ({
  baseMapStyle,
  bottomPadding = 0,
  className,
  children,
  controlsConfig,
  cooperativeGestures = false,
  cursor,
  dimensionsTheme = 'default',
  disableDefaultAttribution = false,
  disableInteraction = false,
  fog,
  handleOnLoad,
  id,
  images = {},
  initialBounds,
  initialCenter,
  is3dActive,
  onDrag,
  onDragEnd,
  onMoveEnd,
  onTouchEnd,
  onTouchMove,
  onZoomEnd,
  overlaysExcludedGeoBoundaryIds,
  overlaysExcludedTrailIds,
  preventReuseMaps = false,
  staticStyleIds,
  terrain
}: Props) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const mapboxAccessToken = getMapboxAccessToken();
  const debugEnabled = useExperiments()['mugen-explore-debug']?.value === 'on';
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const [useCompactAttribution, setUseCompactAttribution] = useState(false);
  const [mapRef, setMapRef] = useState<MapRef>();
  const isMapLoaded = useMapStyleLoaded(mapRef);
  // If no images need to be loaded, we can consider the style data initialized
  const [isStyleDataInitialized, setIsStyleDataInitialized] = useState(Object.keys(images).length === 0);

  const resizeMap = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const { width } = entries[0].target.getBoundingClientRect();
      // At around 704px, the attribution control overflows onto the logo
      setUseCompactAttribution(width < 704);
      mapRef?.resize();
    },
    [mapRef]
  );

  const [observe, disconnect] = useResizeObserver(resizeMap);
  useEffect(() => {
    observe(mapRef?.getContainer());
    return disconnect;
  }, [disconnect, mapRef, observe]);

  const [showMapboxErrorAlert, setShowMapboxErrorAlert] = useState(false);
  useEffect(() => {
    if (!MapboxGL.supported()) {
      // Added to get an idea of how many people run into issues around map support.
      // See https://alltrails.atlassian.net/browse/DISCO-13695 for details.
      logError(`Mapbox GL is not supported - ${notSupportedReason()}`);
      setShowMapboxErrorAlert(true);
    }
  }, []);

  useEffect(() => {
    if (debugEnabled) {
      console.log('[BaseMapReact] isStyleDataInitialized: ', isStyleDataInitialized);
    }
  }, [debugEnabled, isStyleDataInitialized]);

  const onStyleData = useCallback(() => {
    onStyleDataHandler(images, mapRef, isStyleDataInitialized, setIsStyleDataInitialized, debugEnabled);
  }, [debugEnabled, images, mapRef, isStyleDataInitialized, setIsStyleDataInitialized]);

  useEffect(() => {
    // DISCO-19024 - a small race condition in Safari where the style data callback is only fired before the map ref is set
    // This ensures that the style data handler is still fired after the map ref is set to unblock rendering of children
    if (mapRef) {
      onStyleData();
    }
  }, [mapRef, onStyleData]);

  return (
    <>
      <div
        className={classNames(styles.map, className)}
        style={{ '--map-bottom-padding': `${bottomPadding}px` } as CSSProperties}
        data-at-theme-dimensions={dimensionsTheme}
      >
        {/* don't render the map until we have our base styles from mapbox 
      we need the glyphs from the style in order to render the cluster marker text
      */}
        {baseMapStyle && (
          <ReactMapGL
            RTLTextPlugin="https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js"
            id={id}
            ref={mapboxRef => {
              if (!mapboxRef || mapboxRef === mapRef) {
                if (debugEnabled) {
                  console.log('[BaseMapReact] not setting mapRef:');
                  console.log('mapboxRef: ', mapboxRef);
                  console.log('mapRef: ', mapRef);
                  console.log('mapboxRef === mapRef: ', mapboxRef === mapRef);
                }
                return;
              }

              if (debugEnabled) {
                console.log('[BaseMapReact] setting mapRef');
              }

              setMapRef(mapboxRef);
            }}
            attributionControl={disableDefaultAttribution ? false : undefined}
            cursor={cursor}
            logoPosition="bottom-left"
            onLoad={handleOnLoad}
            fog={fog}
            initialViewState={getInitialViewState(initialBounds, initialCenter)}
            interactive={!disableInteraction}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={baseMapStyle}
            onMoveEnd={({ viewState }) => onMoveEndHandler(viewState, mapRef, onMoveEnd)}
            onTouchMove={onTouchMove} // Called when the map is moved via touch (mobile)
            onDrag={onDrag} // Called when the map is moved via drag (desktop)
            onDragEnd={onDragEnd}
            onZoomEnd={onZoomEnd}
            onTouchEnd={onTouchEnd}
            onStyleData={onStyleData}
            // padding={padding} This is intentionally commented out so that it doesn't get added back in - even passing undefined here causes map.setPadding to not work
            reuseMaps={!preventReuseMaps}
            maxPitch={is3dActive ? maxPitchFor3dTerrain : maxPitch}
            cooperativeGestures={cooperativeGestures}
            terrain={terrain}
            {...(controlsConfig || {})}
          >
            {/* Only render Source/Layer children after style is loaded to avoid "Style is not done loading" (react-map-gl#2377) */}
            {isMapLoaded && (
              <>
                {staticStyleIds.map(styleId => (
                  <StaticStyle key={styleId} styleId={styleId} />
                ))}
                {is3dActive && <AtmosphereStyle />}
                <DynamicStyleOverlays excludedTrailIds={overlaysExcludedTrailIds} excludedGeoBoundaryIds={overlaysExcludedGeoBoundaryIds} />
                <ScaleControl maxWidth={80} position="bottom-right" unit={displayMetric ? 'metric' : 'imperial'} />
                {!disableDefaultAttribution && (
                  <AttributionControl
                    // The attribution doesn't respond to changes to the compact prop, so we need to use a key to force a re-render
                    key={useCompactAttribution ? 'compact' : 'full'}
                    compact={useCompactAttribution}
                    customAttribution={`<strong><a href="${getHelpCenterUrl(
                      languageRegionCode,
                      11555324555924
                    )}" target="_blank"> ${intl.formatMessage({ defaultMessage: 'Map legend' })} </a></strong>`}
                    position="bottom-right"
                  />
                )}
                <EmptyPolylineLayers />
                <EmptyChevronLayers />
                {/* Render children only after the style data has been initialized to prevent pop-in and warnings for missing images */}
                {isStyleDataInitialized && children}
              </>
            )}
          </ReactMapGL>
        )}
      </div>
      {showMapboxErrorAlert && (
        <Dialog
          onRequestClose={() => setShowMapboxErrorAlert(false)}
          size="sm"
          title={intl.formatMessage({ defaultMessage: 'Browser or device issue' })}
          body={intl.formatMessage({
            defaultMessage: 'Your browser or device won’t let us display this map. Try updating or switching your browser.'
          })}
          buttons={{
            primary: {
              linkInfo: { href: getHelpCenterUrl(languageRegionCode, 360051234912), target: '_blank' },
              onClick: () => setShowMapboxErrorAlert(false),
              testId: 'troubleshoot',
              text: intl.formatMessage({ defaultMessage: 'Troubleshoot' })
            }
          }}
        />
      )}
    </>
  );
};

export default BaseMapReact;
